import { useState, useEffect } from 'react';
import { Appointment } from '../api';
import { TeamMember } from '../interfaces';
import { updateAppointmentOwner as requestToUpdateAppointmentOwner } from '../api';
import { datadogLogs } from '@datadog/browser-logs';
import { AxiosError } from 'axios';
import { AppointmentReplacementPartialFailedError } from '../api/errors';

interface UseUpdateAppointmentOwnerProps {
  selectedAppointmentID: string;
  selectedTeamMember: TeamMember | null;
}

export type UseUpdateAppointmentOwner = (props: UseUpdateAppointmentOwnerProps) => {
  updateAppointmentOwner: () => Promise<Appointment | false>;
  isUpdatingAppointmentOwner: boolean;
  failedUpdatingAppointmentOwner: boolean;
  updateAppointmentOwnerError: AppointmentReplacementPartialFailedError | Error | AxiosError | null;
  resetUpdateAppointmentOwner: () => void;
};

export const useUpdateAppointmentOwner: UseUpdateAppointmentOwner = ({
  selectedTeamMember = null,
  selectedAppointmentID = '',
}) => {
  const [selectedNewAppointmentOwner, setSelectedNewAppointmentOwner] =
    useState<UseUpdateAppointmentOwnerProps['selectedTeamMember']>(selectedTeamMember);
  const [appointmentID, setAppointmentID] =
    useState<UseUpdateAppointmentOwnerProps['selectedAppointmentID']>(selectedAppointmentID);
  const [isUpdatingAppointmentOwner, setIsUpdatingAppointmentOwner] =
    useState<ReturnType<UseUpdateAppointmentOwner>['isUpdatingAppointmentOwner']>(false);
  const [failedUpdatingAppointmentOwner, setFailedUpdatingAppointmentOwner] =
    useState<ReturnType<UseUpdateAppointmentOwner>['failedUpdatingAppointmentOwner']>(false);
  const [updateAppointmentOwnerError, setUpdateAppointmentOwnerError] =
    useState<ReturnType<UseUpdateAppointmentOwner>['updateAppointmentOwnerError']>(null);

  useEffect(() => {
    setSelectedNewAppointmentOwner(selectedTeamMember);
  }, [selectedTeamMember, setSelectedNewAppointmentOwner]);

  useEffect(() => {
    setAppointmentID(selectedAppointmentID);
  }, [selectedAppointmentID, setAppointmentID]);

  const updateAppointmentOwner = async (): Promise<Appointment | false> => {
    try {
      if (!selectedNewAppointmentOwner || !selectedAppointmentID) return false;

      setFailedUpdatingAppointmentOwner(false);
      setIsUpdatingAppointmentOwner(true);

      const updatedAppointment = await requestToUpdateAppointmentOwner(appointmentID, selectedNewAppointmentOwner.id);

      datadogLogs.logger.info(
        `${updatedAppointment.id} has been updated successfully with appointment owner ${updatedAppointment?.broker?.id}.`
      );

      setIsUpdatingAppointmentOwner(false);

      return updatedAppointment;
    } catch (error) {
      datadogLogs.logger.error(`An error occurred while updating the appointment owner: ${JSON.stringify(error)}.`);
      setUpdateAppointmentOwnerError(error as AppointmentReplacementPartialFailedError | Error | AxiosError);
      setIsUpdatingAppointmentOwner(false);
      setSelectedNewAppointmentOwner(null);
      setFailedUpdatingAppointmentOwner(true);

      return false;
    }
  };

  const resetUpdateAppointmentOwner = () => {
    setSelectedNewAppointmentOwner(null);
    setFailedUpdatingAppointmentOwner(false);
    setUpdateAppointmentOwnerError(null);
    setIsUpdatingAppointmentOwner(false);
  };

  return {
    updateAppointmentOwner,
    isUpdatingAppointmentOwner,
    failedUpdatingAppointmentOwner,
    updateAppointmentOwnerError,
    resetUpdateAppointmentOwner,
  };
};
